import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";
import { useProperty } from "@/context/PropertyContext";
import { Printer, Link as LinkIcon, QrCode as QrIcon } from "lucide-react";
import FoxLogo from "@/components/FoxLogo";

/**
 * /app/qr-poster — Generate a printable A4 poster with a QR code the host
 * can hang in reception. Guests scan → phone opens the target URL.
 *
 * Defaults to the public guide (/guida) but the host can point the QR
 * anywhere: their WiFi setup, house rules, self check-in landing, etc.
 */
export default function QrPoster() {
  const { t, i18n } = useTranslation();
  const { current } = useProperty();

  // Default: public guide with the current UI language.
  const guideUrl = useMemo(() => {
    const origin = window.location.origin;
    return `${origin}/guida`;
  }, []);

  const [url, setUrl] = useState(guideUrl);
  const [headline, setHeadline] = useState(t("qr_poster.default_headline", "Benvenuto"));
  const [subline, setSubline] = useState(
    t("qr_poster.default_subline", "Scansiona per la guida in 5 lingue")
  );

  const propertyName = current?.name || t("qr_poster.your_property", "La tua struttura");

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="qr-poster-page">
      {/* Controls (hidden on print) */}
      <div className="print:hidden">
        <p className="text-xs tracked text-blue-700 mb-2 inline-flex items-center gap-1.5">
          <QrIcon className="w-3.5 h-3.5" /> {t("qr_poster.eyebrow", "QR CODE STAMPABILE")}
        </p>
        <h1 className="font-heading font-semibold text-4xl tracking-tight mb-2">
          {t("qr_poster.title", "Poster QR per la reception")}
        </h1>
        <p className="text-slate-600 mb-8 max-w-2xl">
          {t(
            "qr_poster.subtitle",
            "Stampa questo poster in A4 e appendilo in reception, alla porta o in camera. Gli ospiti scansionano il QR con la fotocamera del telefono e aprono la pagina che vuoi."
          )}
        </p>

        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs tracked text-slate-500 flex items-center gap-1.5">
                <LinkIcon className="w-3 h-3" /> {t("qr_poster.url_label", "URL di destinazione")}
              </span>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="qr-url-input"
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-700"
                placeholder="https://..."
              />
              <span className="text-xs text-slate-500 mt-1 block">
                {t(
                  "qr_poster.url_hint",
                  "Predefinito: la guida pubblica. Puoi puntare a self check-in, WiFi, regole della casa, Instagram, TripAdvisor…"
                )}
              </span>
            </label>

            <label className="block">
              <span className="text-xs tracked text-slate-500">
                {t("qr_poster.headline_label", "Titolo del poster")}
              </span>
              <input
                type="text"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                data-testid="qr-headline-input"
                maxLength={40}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-700"
              />
            </label>

            <label className="block">
              <span className="text-xs tracked text-slate-500">
                {t("qr_poster.subline_label", "Istruzioni per l'ospite")}
              </span>
              <textarea
                value={subline}
                onChange={(e) => setSubline(e.target.value)}
                data-testid="qr-subline-input"
                rows={2}
                maxLength={120}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-700"
              />
            </label>

            <button
              type="button"
              onClick={() => window.print()}
              data-testid="qr-print-btn"
              className="mt-2 inline-flex items-center gap-2 bg-blue-700 text-white px-5 py-3 rounded-lg hover:bg-blue-700 font-medium"
            >
              <Printer className="w-4 h-4" /> {t("qr_poster.print", "Stampa poster A4")}
            </button>

            <div className="pt-6 border-t border-slate-200 text-sm text-slate-600 space-y-2">
              <p className="font-medium text-stone-800">
                {t("qr_poster.tips_title", "Consigli per la stampa")}
              </p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>{t("qr_poster.tip_1", "Carta 200 g/m² o superiore per durare")}</li>
                <li>{t("qr_poster.tip_2", "Plastifica la stampa se la metti all'esterno")}</li>
                <li>{t("qr_poster.tip_3", "Prova il QR con il tuo telefono prima di appenderlo")}</li>
                <li>{t("qr_poster.tip_4", "Aggiornabile: rigenera se cambi la destinazione")}</li>
              </ul>
            </div>
          </div>

          {/* Live preview */}
          <div className="flex justify-center">
            <div className="w-full max-w-md aspect-[210/297] bg-white border-2 border-slate-200 shadow-sm">
              <PosterContent
                url={url}
                headline={headline}
                subline={subline}
                propertyName={propertyName}
                lang={i18n.language}
                scale={0.55}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Print-only full poster */}
      <div className="hidden print:block">
        <PosterContent
          url={url}
          headline={headline}
          subline={subline}
          propertyName={propertyName}
          lang={i18n.language}
          scale={1}
        />
      </div>

      {/* Print CSS: hide everything except the poster and use A4 */}
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { background: white; }
          nav, aside, header, [data-testid="billing-banner"], .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function PosterContent({ url, headline, subline, propertyName, lang, scale = 1 }) {
  // Multi-language "Scan with camera" hints so any guest understands
  const scanHints = {
    it: "Inquadra con la fotocamera",
    en: "Point your camera at the code",
    fr: "Pointez la caméra vers le code",
    de: "Kamera auf den Code richten",
    zh: "用手机相机扫描",
  };

  return (
    <div
      className="w-full h-full flex flex-col items-center justify-between p-10 bg-white"
      style={{ fontSize: `${scale}em` }}
      data-testid="qr-poster-content"
    >
      {/* Top: brand + property */}
      <div className="text-center w-full">
        <div className="inline-flex items-center gap-3 mb-2">
          <FoxLogo size={Math.round(48 * scale)} />
          <span className="font-heading font-semibold text-3xl tracking-tight">
            Xenia<span className="text-blue-700">.</span>
          </span>
        </div>
        <p className="text-xs tracked text-blue-700 uppercase">{propertyName}</p>
      </div>

      {/* Middle: headline + QR */}
      <div className="text-center flex-1 flex flex-col items-center justify-center w-full py-6">
        <h1 className="font-heading font-semibold text-6xl tracking-tight mb-3 text-slate-900">
          {headline}
        </h1>
        <p className="text-lg text-slate-700 max-w-md mb-8">{subline}</p>

        <div className="bg-white p-6 border border-slate-200 shadow-sm rounded-lg">
          <QRCode
            value={url || "https://xeniaggr.com/guida"}
            size={Math.round(320 * scale)}
            fgColor="#1c1917"
            bgColor="#FFFFFF"
            level="M"
          />
        </div>

        <p className="text-xs tracked text-slate-500 mt-4 uppercase">
          {scanHints[lang] || scanHints.it}
        </p>
      </div>

      {/* Bottom: multilingual mini hints */}
      <div className="w-full text-center border-t border-slate-200 pt-4 text-xs text-slate-500">
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-1">
          <span>IT · {scanHints.it}</span>
          <span>EN · {scanHints.en}</span>
          <span>FR · {scanHints.fr}</span>
          <span>DE · {scanHints.de}</span>
          <span>ZH · {scanHints.zh}</span>
        </div>
        <p className="mt-3 text-[10px] text-slate-400">
          xeniaregistry.com · The Global Guest Registry
        </p>
      </div>
    </div>
  );
}
